import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Divider,
  CircularProgress,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  AppBar,
  Toolbar,
  Menu,
  MenuItem,
  Drawer,
  ListItemIcon,
  Chip,
  Card,
  CardContent,
  LinearProgress,
  Alert
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Medication,
  Message,
  Person,
  Settings,
  Logout,
  Menu as MenuIcon,
  Notifications,
  CheckCircle,
  Cancel,
  AccessTime,
  BarChart,
  TrendingUp,
  Psychology
} from '@mui/icons-material';
import { 
  BarChart as RechartsBarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ReferenceLine
} from 'recharts';

// Import logo
import avenLogo from '../assets/images/aven-logo.svg';

// Mock data for development
const mockMedicationData = [
  { name: 'Ozempic', adherence: 85 },
  { name: 'Metformin', adherence: 92 },
  { name: 'Rybelsus', adherence: 78 }
];

const mockReminderData = [
  { 
    id: 1, 
    medication: 'Ozempic', 
    time: '08:00 AM', 
    status: 'completed', 
    date: new Date().toLocaleDateString() 
  },
  { 
    id: 2, 
    medication: 'Metformin', 
    time: '01:00 PM', 
    status: 'upcoming', 
    date: new Date().toLocaleDateString() 
  },
  { 
    id: 3, 
    medication: 'Rybelsus', 
    time: '07:00 PM', 
    status: 'upcoming', 
    date: new Date().toLocaleDateString() 
  }
];

const mockMessageData = [
  { 
    id: 1, 
    content: 'How are you feeling today?', 
    sender: 'ai', 
    timestamp: new Date(Date.now() - 3600000).toISOString() 
  },
  { 
    id: 2, 
    content: 'I had some nausea after taking my medication.', 
    sender: 'user', 
    timestamp: new Date(Date.now() - 3500000).toISOString() 
  },
  { 
    id: 3, 
    content: 'I understand that can be uncomfortable. Try taking your medication with food to reduce nausea. Would you like some other tips for managing this side effect?', 
    sender: 'ai', 
    timestamp: new Date(Date.now() - 3400000).toISOString() 
  }
];

// Mock weight data for the weight loss chart (in lbs)
const mockWeightData = [
  { date: '2025-01-01', weight: 210.0 },
  { date: '2025-01-15', weight: 206.8 },
  { date: '2025-02-01', weight: 201.7 },
  { date: '2025-02-15', weight: 198.9 },
  { date: '2025-03-01', weight: 195.5 },
  { date: '2025-03-15', weight: 192.5 },
  { date: '2025-03-30', weight: 189.8 }
];

const mockUserStats = {
  adherenceRate: 87,
  weightLossTotal: 20.2, // lbs
  weightLossPercentage: 9.6, // %
  daysOnMedication: 89
};

const mockCoachingStats = {
  totalSessions: 24,
  averageSentiment: 0.72,
  topTechniques: [
    { name: 'Cognitive Restructuring', count: 8 },
    { name: 'Behavioral Activation', count: 6 },
    { name: 'Mindfulness', count: 5 }
  ]
};

// Colors for charts
const COLORS = ['#2e7d32', '#1976d2', '#ff9800', '#e91e63'];

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [medicationData, setMedicationData] = useState([]);
  const [reminderData, setReminderData] = useState([]);
  const [messageData, setMessageData] = useState([]);
  const [userStats, setUserStats] = useState({});
  const [coachingStats, setCoachingStats] = useState({});
  
  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  
  // Handle menu open
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  
  // Handle menu close
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  
  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleManageSubscription = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      const res = await axios.post(
        `${API_BASE_URL}/api/v1/payments/create-portal-session`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res?.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      setError('Unable to open subscription portal. Please contact support.');
    } catch (err) {
      console.error('Unable to open subscription portal', err);
      setError('Unable to open subscription portal. Please try again later.');
    }
  };
  
  // Toggle drawer
  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };
  
  // Fetch data from API or use mock data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      try {
        // Check if we're in development mode
        const isDevelopment = process.env.NODE_ENV === 'development';
        
        if (isDevelopment) {
          // Use mock data for development
          setTimeout(() => {
            setUserData({
              id: 1,
              fullName: 'John Doe',
              email: 'john.doe@example.com',
              phoneNumber: '+1234567890'
            });
            setMedicationData(mockMedicationData);
            setReminderData(mockReminderData);
            setMessageData(mockMessageData);
            setUserStats(mockUserStats);
            setCoachingStats(mockCoachingStats);
            setLoading(false);
          }, 1000);
        } else {
          // Fetch real data from API
          const token = localStorage.getItem('token');
          
          if (!token) {
            navigate('/login');
            return;
          }
          
          const config = {
            headers: { Authorization: `Bearer ${token}` }
          };
          
          // Fetch user data
          const userResponse = await axios.get(`${API_BASE_URL}/api/v1/users/me`, config);
          setUserData(userResponse.data);
          
          // Fetch medication data
          const medicationResponse = await axios.get(`${API_BASE_URL}/api/v1/medications`, config);
          setMedicationData(medicationResponse.data);
          
          // Fetch reminder data
          const reminderResponse = await axios.get(`${API_BASE_URL}/api/v1/reminders`, config);
          setReminderData(reminderResponse.data);
          
          // Fetch message data
          const messageResponse = await axios.get(`${API_BASE_URL}/api/v1/messages`, config);
          setMessageData(messageResponse.data);
          
          // Fetch user stats
          const userStatsResponse = await axios.get(`${API_BASE_URL}/api/v1/stats/users`, config);
          setUserStats(userStatsResponse.data);
          
          // Fetch coaching stats
          const coachingStatsResponse = await axios.get(`${API_BASE_URL}/api/v1/stats/coaching`, config);
          setCoachingStats(coachingStatsResponse.data);
          
          setLoading(false);
        }
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load dashboard data. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchData();
  }, [navigate]);
  
  // Render loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh'
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading your dashboard...
        </Typography>
      </Box>
    );
  }
  
  // Sidebar drawer content
  const drawerContent = (
    <Box sx={{ width: 250 }}>
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
        }}
      >
        <img src={avenLogo} alt="Aven Health Logo" height={40} />
      </Box>
      
      <List>
        <ListItem button selected>
          <ListItemIcon>
            <DashboardIcon color="primary" />
          </ListItemIcon>
          <ListItemText primary="Dashboard" />
        </ListItem>
        
        <ListItem button>
          <ListItemIcon>
            <Medication />
          </ListItemIcon>
          <ListItemText primary="Medications" />
        </ListItem>
        
        <ListItem button>
          <ListItemIcon>
            <Message />
          </ListItemIcon>
          <ListItemText primary="Messages" />
        </ListItem>
        
        <ListItem button>
          <ListItemIcon>
            <Psychology />
          </ListItemIcon>
          <ListItemText primary="AI Coach" />
        </ListItem>
        
        <Divider sx={{ my: 2 }} />
        
        <ListItem button>
          <ListItemIcon>
            <Person />
          </ListItemIcon>
          <ListItemText primary="Profile" />
        </ListItem>
        
        <ListItem button>
          <ListItemIcon>
            <Settings />
          </ListItemIcon>
          <ListItemText primary="Settings" />
        </ListItem>
        
        <ListItem button onClick={handleLogout}>
          <ListItemIcon>
            <Logout />
          </ListItemIcon>
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Box>
  );
  
  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      {/* Sidebar Drawer */}
      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
      >
        {drawerContent}
      </Drawer>
      
      {/* Main Content */}
      <Box sx={{ flexGrow: 1 }}>
        {/* App Bar */}
        <AppBar 
          position="static" 
          color="default" 
          elevation={0}
          sx={{ 
            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
            bgcolor: 'white'
          }}
        >
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src={avenLogo} alt="Aven Health Logo" height={30} />
            </Box>
            
            <Box sx={{ flexGrow: 1 }} />
            
            <IconButton color="inherit">
              <Notifications />
            </IconButton>
            
            <IconButton
              onClick={handleMenuOpen}
              color="inherit"
              sx={{ ml: 1 }}
            >
              <Avatar 
                sx={{ 
                  width: 32, 
                  height: 32, 
                  bgcolor: 'primary.main' 
                }}
              >
                {userData?.fullName?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
            
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
              <MenuItem onClick={handleMenuClose}>Settings</MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleManageSubscription();
                }}
              >
                Manage Subscription
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Toolbar>
        </AppBar>
        
        {/* Dashboard Content */}
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}
          
          {/* Welcome Section */}
          <Paper
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              mb: 4,
              borderRadius: 2,
              background: 'linear-gradient(45deg, #2e7d32 30%, #43a047 90%)',
              color: 'white'
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="h4" gutterBottom>
                  Welcome back, {userData?.fullName?.split(' ')[0] || 'User'}!
                </Typography>
                <Typography variant="body1">
                  Here's an overview of your health journey.
                </Typography>
              </Box>
              <Button
                variant="contained"
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.9)'
                  }
                }}
              >
                View Health Report
              </Button>
            </Box>
          </Paper>
          
          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Medication Adherence
                </Typography>
                <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                  <CircularProgress
                    variant="determinate"
                    value={userStats.adherenceRate || 0}
                    size={80}
                    thickness={5}
                    sx={{ color: 'primary.main' }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                    }}
                  >
                    <Typography variant="h5" component="div" color="text.primary">
                      {userStats.adherenceRate || 0}%
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Last 30 days
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Upcoming Reminders
                </Typography>
                <Typography variant="h3" color="text.primary">
                  {reminderData.filter(r => r.status === 'upcoming').length || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Today
                </Typography>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Weight Loss
                </Typography>
                <Typography variant="h3" color="text.primary">
                  {userStats.weightLossTotal || 0} lbs
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                  <TrendingUp color="primary" fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2" color="primary">
                    {userStats.weightLossPercentage || 0}% from starting weight
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            
            <Grid item xs={12} sm={6} md={3}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  <strong>Days on Medication</strong>
                </Typography>
                <Typography variant="h3" color="text.primary">
                  {userStats.daysOnMedication || 0}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  Your GLP-1 journey
                </Typography>
              </Paper>
            </Grid>
          </Grid>
          
          {/* Charts and Lists */}
          <Grid container spacing={3}>
            {/* Weight Loss Chart */}
            <Grid item xs={12} md={8}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  height: 350
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Weight Loss Progress
                </Typography>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={mockWeightData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 5,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis 
                      dataKey="date" 
                      tickFormatter={(date) => {
                        const d = new Date(date);
                        return `${d.getMonth()+1}/${d.getDate()}`;
                      }}
                    />
                    <YAxis 
                      domain={['dataMin - 5', 'dataMax + 5']} 
                      label={{ value: 'Weight (lbs)', angle: -90, position: 'insideLeft' }} 
                    />
                    <Tooltip 
                      formatter={(value) => [`${value} lbs`, 'Weight']}
                      labelFormatter={(date) => {
                        const d = new Date(date);
                        return d.toLocaleDateString();
                      }}
                    />
                    <ReferenceLine y={mockWeightData[0].weight} stroke="#ff9800" strokeDasharray="3 3" label="Starting Weight" />
                    <Line 
                      type="monotone" 
                      dataKey="weight" 
                      stroke="#2e7d32" 
                      strokeWidth={3}
                      dot={{ r: 6 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Paper>
            </Grid>
            
            {/* Medication Adherence Chart */}
            <Grid item xs={12} md={4}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  height: 350
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Medication Adherence
                </Typography>
                <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart
                      data={medicationData}
                      layout="vertical"
                      margin={{
                        top: 20,
                        right: 30,
                        left: 60,
                        bottom: 5,
                      }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} />
                      <YAxis dataKey="name" type="category" width={100} />
                      <Tooltip formatter={(value) => [`${value}%`, 'Adherence Rate']} />
                      <ReferenceLine x={80} stroke="#ff9800" label="Target" />
                      <Bar 
                        dataKey="adherence" 
                        name="Adherence Rate" 
                        fill="#2e7d32" 
                        radius={[0, 4, 4, 0]}
                        barSize={30}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>
            
            {/* Upcoming Reminders */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Today's Medication Schedule
                </Typography>
                <List>
                  {reminderData.map((reminder) => (
                    <ListItem
                      key={reminder.id}
                      secondaryAction={
                        reminder.status === 'completed' ? (
                          <Chip 
                            icon={<CheckCircle />} 
                            label="Taken" 
                            color="primary" 
                            size="small" 
                          />
                        ) : (
                          <Chip 
                            icon={<AccessTime />} 
                            label="Upcoming" 
                            color="secondary" 
                            size="small" 
                          />
                        )
                      }
                      sx={{
                        bgcolor: reminder.status === 'completed' ? 'rgba(46, 125, 50, 0.1)' : 'transparent',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: reminder.status === 'completed' ? 'primary.main' : 'secondary.main' }}>
                          <Medication />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={reminder.medication}
                        secondary={`Scheduled for ${reminder.time}`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
            
            {/* Recent Messages */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Recent Conversations
                </Typography>
                <List>
                  {messageData.map((message) => (
                    <ListItem
                      key={message.id}
                      alignItems="flex-start"
                      sx={{
                        bgcolor: message.sender === 'ai' ? 'rgba(25, 118, 210, 0.1)' : 'transparent',
                        borderRadius: 1,
                        mb: 1
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: message.sender === 'ai' ? 'secondary.main' : 'primary.main' }}>
                          {message.sender === 'ai' ? 'AI' : userData?.fullName?.charAt(0) || 'U'}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={message.sender === 'ai' ? 'AI Coach' : 'You'}
                        secondary={
                          <>
                            <Typography
                              component="span"
                              variant="body2"
                              color="text.primary"
                            >
                              {message.content}
                            </Typography>
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'block', mt: 0.5 }}
                            >
                              {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </Typography>
                          </>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  sx={{ mt: 2 }}
                >
                  View All Conversations
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Dashboard;
